import { Nullable } from '@src/common-utils/Models';
import { CognitoGroup, PatchPreferredDeliveryAddress, UserInfo } from '@src/common-utils/AuthModels';

export type ImageDTO = {
  name: string;
  url: string;
};

export type DiscountType = 'BUY_X_GET_Y_FREE' | 'PERCENTAGE';

export type ProductDiscount = {
  description: string;
  discountType: DiscountType;
  discountValue: number;
  discountYValue?: Nullable<number>;
};

export type ProductDTO = {
  code: string;
  name: string;
  description?: Nullable<string>;
  specification: string[];
  packing: string;
  unit: string;
  price: number;
  images: ImageDTO[];
  discount?: Nullable<ProductDiscount>;
};

// ---------------------------------------------------------------------------------------------
// Admin product management (SystemAdmin only) - mirrors dental-ware-services
// services/lambda/common-lib/ProductAdminModels.ts. These types and constants are duplicated on purpose:
// keep both sides in step (see .github/skills/cross-repo-change).
// ---------------------------------------------------------------------------------------------
export const PRODUCT_CODE_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;
export const PRICE_LEVELS = ['A', 'B', 'C', 'D', 'E'] as const;
export type PriceLevel = (typeof PRICE_LEVELS)[number];

/** Allowed image content types and the extensions that may go with each. SVG is deliberately not allowed. */
export const IMAGE_ALLOWED_TYPES: Record<string, readonly string[]> = {
  'image/jpeg': ['jpg', 'jpeg'],
  'image/png': ['png'],
  'image/gif': ['gif'],
  'image/webp': ['webp'],
};
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024; // per file
export const IMAGE_MAX_PER_PRODUCT = 10;
export const IMAGE_MIN_PER_PRODUCT = 1;
export const PRODUCT_MAX_PRICE = 1_000_000;

export type AdminProductDTO = {
  code: string;
  name: string;
  description?: Nullable<string>;
  specification: string[];
  packing: string;
  unit: string;
  listPrice: number;
  priceLevels?: Nullable<Partial<Record<PriceLevel, number>>>;
  images: ImageDTO[];
  discount?: Nullable<ProductDiscount>;
  version: number;
  created?: string;
  updated?: string;
};

export type AdminProductsResponse = {
  products: AdminProductDTO[];
  lastEvaluatedKey?: Nullable<string>;
};

export type CreateProductDTO = {
  code: string;
  name: string;
  description?: Nullable<string>;
  specification: string[];
  packing: string;
  unit: string;
  listPrice: number;
  priceLevels?: Nullable<Partial<Record<PriceLevel, number>>>;
  /** Ordered image keys (first = primary): keys returned by requestImageUploadUrls */
  images: string[];
  discount?: Nullable<ProductDiscount>;
};

/** Every field but `version` is optional; null clears description, priceLevels and discount. `code` is immutable. */
export type UpdateProductDTO = Partial<Omit<CreateProductDTO, 'code'>> & { version: number };

export type ImageUploadFileRequest = { fileName: string; contentType: string; size: number };

export type ImageUploadUrl = {
  key: string;
  uploadUrl: string;
  headers: Record<string, string>;
  expiresInSeconds: number;
};

export type ImageUploadUrlsResponse = { uploads: ImageUploadUrl[] };

export type LocalStoreCartItem = {
  productCode: string;
  quantity: number;
};

export type CartItem = LocalStoreCartItem & {
  name: string;
  packing: string;
  unit: string;
  price: number;
  itemTotals: number;
  imageURL?: string;
  discount?: Nullable<ProductDiscount>;
};

export type DiscountCartItem = {
  productCode: string;
  productName: string;
  discountAmount: number;
  description: string;
};

export type MyCart = {
  userId: string;
  userEmail: string;
  cartItems?: Nullable<CartItem[]>;
};

export type OrderItem = {
  productCode: string;
  quantity: number;
  // price: number;
};

export type CreateOrderDTO = {
  deliveryAddress: string;
  deliveryClinicId?: Nullable<string>;
  orderItems: OrderItem[];
};

export type CreateOrderResponse = {
  id: string;
  userId: string;
  userEmail: string;
  status: string;
  totals: number;
};

export type UserSearchResponse = {
  users: UserInfo[];
  lastEvaluatedKey?: Nullable<string>;
};

export type PatchUserDTO = {
  priceLevel?: string;
  groups?: CognitoGroup[];
  preferredDeliveryAddress?: Nullable<PatchPreferredDeliveryAddress>;
};

export type ClinicDTO = {
  id: string;
  name: string;
  phoneNumber: string;
  address: string;
  owner: string;
};

export type GetClinicsResponse = {
  clinics: ClinicDTO[];
};

export type CreateClinicDTO = {
  name: string;
  phoneNumber: string;
  address: string;
  addOwnerAsMember?: boolean;
};

export type AddClinicMemberDTO = {
  memberEmail: string;
};

export enum ClinicMemberStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
}

export type ClinicMemberDTO = {
  clinicId: string;
  member: {
    id: string;
    email: string;
  };
  status: ClinicMemberStatus;
};

export type GetClinicMembersResponse = {
  clinicMembers: ClinicMemberDTO[];
};

export type ClinicMemberIdentifier = {
  clinicId: string;
  memberId: string;
};

export type MemberClinicDTO = {
  memberId: string;
  clinic: ClinicDTO;
  status: ClinicMemberStatus;
  isDefault?: boolean;
};

export type PatchMemberClinicDTO = {
  memberId: string;
  clinicId: string;
  status?: ClinicMemberStatus;
  isDefault?: boolean;
};

export type GetMemberClinicsResponse = {
  memberClinics: MemberClinicDTO[];
};

export const StatusDisplay: Record<ClinicMemberStatus, string> = {
  [ClinicMemberStatus.PENDING]: 'Pending',
  [ClinicMemberStatus.ACCEPTED]: 'Accepted',
  [ClinicMemberStatus.DECLINED]: 'Declined',
};

export type DeliveryAddress = {
  addressType: string;
  address: string;
  clinicId?: Nullable<string>;
};
